import React, { useMemo, useState } from "react";
import { Button, Group, Select, Stack, Text } from "@mantine/core";
import { IconPencil } from "@tabler/icons-react";
import { useSalon } from "../../../context/SalonContext";
import { useProducts } from "../../../context/ProductsContext";
import { useApp } from "../../../context/AppContext";
import usePermissions from "../../../hooks/usePermissions";
import { SalonService } from "../../../types/salon";
import { Product } from "../../../types/products";
import {
  StandardTable,
  StandardTableColumn,
  StandardTableSortDirection,
} from "../../common/StandardTable/StandardTable";
import { formatCurrency } from "../../../utils/cashDrawerUtils";
import BusinessChargeEditModal, {
  ChargeChange,
} from "./BusinessChargeEditModal";

/**
 * Commissions › Business Charges. Overview + bulk editor for the fixed "business
 * charge" (house cut) taken from each salon service / product before staff
 * commission is calculated. A dropdown toggles between the Services and Products
 * tables (one at a time); the "Edit" button next to it opens a modal that
 * bulk-updates the shown catalog in one call, then refreshes the services /
 * products context so local state stays synced.
 *
 * Rendered as the Business Charges tab of the CommissionsManagement page.
 */

const INTRO =
  "The business charge is the portion of each sale kept by the business. " +
  "Whether it reduces staff commission before or after the commission rate is " +
  "applied depends on the Business Charge Deduction option in Advanced Settings.";

type Catalog = "services" | "products";

const CATALOG_OPTIONS = [
  { value: "services", label: "Services" },
  { value: "products", label: "Products" },
];

type Accessor<T> = (row: T) => string | number;

/** Sort a copy of `rows` by the accessor for `field`, honoring direction. */
function useSortedRows<T>(
  rows: T[] | null | undefined,
  accessors: Record<string, Accessor<T>>,
  sortField: string,
  sortDirection: StandardTableSortDirection,
): T[] {
  return useMemo(() => {
    const arr = [...(rows || [])];
    const get = accessors[sortField];
    if (!get) return arr;
    arr.sort((a, b) => {
      const av = get(a);
      const bv = get(b);
      let cmp: number;
      if (typeof av === "number" || typeof bv === "number") {
        cmp = (Number(av) || 0) - (Number(bv) || 0);
      } else {
        cmp = String(av).localeCompare(String(bv));
      }
      return sortDirection === "asc" ? cmp : -cmp;
    });
    return arr;
    // accessors is a stable module-level constant per table.
  }, [rows, accessors, sortField, sortDirection]);
}

const SERVICE_ACCESSORS: Record<string, Accessor<SalonService>> = {
  name: (s) => (s.name || "").toLowerCase(),
  service_type: (s) => (s.service_type || "").toLowerCase(),
  default_price: (s) => s.default_price ?? 0,
};

const PRODUCT_ACCESSORS: Record<string, Accessor<Product>> = {
  name: (p) => (p.name || "").toLowerCase(),
  category: (p) => (p.category || "").toLowerCase(),
  company: (p) => (p.company || "").toLowerCase(),
  purchase_price: (p) => p.purchase_price ?? 0,
  list_price: (p) => p.list_price ?? 0,
};

const useTableSort = (defaultField: string) => {
  const [sortField, setSortField] = useState<string>(defaultField);
  const [sortDirection, setSortDirection] =
    useState<StandardTableSortDirection>("asc");
  const onSort = (key: string): void => {
    if (sortField === key) {
      setSortDirection((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortField(key);
      setSortDirection("asc");
    }
  };
  return { sortField, sortDirection, onSort };
};

/** Props shared by the two catalog tables — the edit modal is driven from the
 *  parent so its trigger sits on the same row as the catalog dropdown. */
interface CatalogTableProps {
  editOpen: boolean;
  onCloseEdit: () => void;
}

const ServiceChargesTable: React.FC<CatalogTableProps> = ({
  editOpen,
  onCloseEdit,
}) => {
  const { services, businessInfo, refreshServices } = useSalon() as {
    services: SalonService[] | null;
    businessInfo: { id: string } | null;
    refreshServices: () => Promise<unknown>;
  };
  const { bulkUpdateBusinessCharges } = useApp();

  const { sortField, sortDirection, onSort } = useTableSort("name");
  const rows = useSortedRows(
    services,
    SERVICE_ACCESSORS,
    sortField,
    sortDirection,
  );

  const modalContextColumns: StandardTableColumn<SalonService>[] = [
    {
      key: "service_type",
      label: "Type",
      render: (s) => (
        <Text size="sm" c={s.service_type ? undefined : "dimmed"}>
          {s.service_type || "—"}
        </Text>
      ),
    },
    {
      key: "default_price",
      label: "Price",
      align: "right",
      render: (s) => <Text size="sm">{formatCurrency(s.default_price)}</Text>,
    },
  ];

  const handleSave = async (changes: ChargeChange[]): Promise<void> => {
    if (!businessInfo?.id) return;
    await bulkUpdateBusinessCharges(businessInfo.id, {
      services: changes.map((c) => ({
        id: c.id,
        default_business_charge: c.charge,
      })),
    });
    // Keep local state in sync after every change.
    await refreshServices();
  };

  const columns: StandardTableColumn<SalonService>[] = [
    {
      key: "name",
      label: "Service",
      sortable: true,
      render: (s) => (
        <Text size="sm" fw={500}>
          {s.name}
        </Text>
      ),
    },
    {
      key: "service_type",
      label: "Type",
      sortable: true,
      render: (s) => (
        <Text size="sm" c={s.service_type ? undefined : "dimmed"}>
          {s.service_type || "—"}
        </Text>
      ),
    },
    {
      key: "default_price",
      label: "Price",
      align: "right",
      sortable: true,
      render: (s) => <Text size="sm">{formatCurrency(s.default_price)}</Text>,
    },
    {
      key: "default_business_charge",
      label: "Business charge",
      align: "right",
      render: (s) => (
        <Text size="sm" fw={500}>
          {formatCurrency(s.default_business_charge ?? 0)}
        </Text>
      ),
    },
  ];

  return (
    <>
      <StandardTable<SalonService>
        columns={columns}
        data={rows}
        getRowKey={(s) => s.id}
        minWidth={640}
        maxHeight="55dvh"
        stickyHeader
        ariaLabel="Service business charges"
        sortField={sortField}
        sortDirection={sortDirection}
        onSort={onSort}
        emptyState={
          <Text c="dimmed" size="sm">
            This salon has no services yet.
          </Text>
        }
      />
      <BusinessChargeEditModal<SalonService>
        opened={editOpen}
        onClose={onCloseEdit}
        title="Edit service business charges"
        rows={rows}
        getRowKey={(s) => s.id}
        getName={(s) => s.name}
        getCurrentCharge={(s) => s.default_business_charge ?? 0}
        contextColumns={modalContextColumns}
        onSave={handleSave}
        minWidth={520}
      />
    </>
  );
};

const ProductChargesTable: React.FC<CatalogTableProps> = ({
  editOpen,
  onCloseEdit,
}) => {
  const { products, fetchProducts } = useProducts();
  const { businessInfo } = useSalon() as {
    businessInfo: { id: string } | null;
  };
  const { bulkUpdateBusinessCharges } = useApp();

  const { sortField, sortDirection, onSort } = useTableSort("name");
  const rows = useSortedRows(
    products,
    PRODUCT_ACCESSORS,
    sortField,
    sortDirection,
  );

  const modalContextColumns: StandardTableColumn<Product>[] = [
    {
      key: "category",
      label: "Category",
      render: (p) => (
        <Text size="sm" c={p.category ? undefined : "dimmed"}>
          {p.category || "—"}
        </Text>
      ),
    },
    {
      key: "company",
      label: "Brand",
      render: (p) => (
        <Text size="sm" c={p.company ? undefined : "dimmed"}>
          {p.company || "—"}
        </Text>
      ),
    },
    {
      key: "list_price",
      label: "Sell price",
      align: "right",
      render: (p) => <Text size="sm">{formatCurrency(p.list_price)}</Text>,
    },
  ];

  const handleSave = async (changes: ChargeChange[]): Promise<void> => {
    if (!businessInfo?.id) return;
    await bulkUpdateBusinessCharges(businessInfo.id, {
      products: changes.map((c) => ({ id: c.id, business_charge: c.charge })),
    });
    // Keep local state in sync after every change.
    await fetchProducts();
  };

  const columns: StandardTableColumn<Product>[] = [
    {
      key: "name",
      label: "Product",
      sortable: true,
      render: (p) => (
        <Text size="sm" fw={500}>
          {p.name}
        </Text>
      ),
    },
    {
      key: "category",
      label: "Category",
      sortable: true,
      render: (p) => (
        <Text size="sm" c={p.category ? undefined : "dimmed"}>
          {p.category || "—"}
        </Text>
      ),
    },
    {
      key: "company",
      label: "Brand",
      sortable: true,
      render: (p) => (
        <Text size="sm" c={p.company ? undefined : "dimmed"}>
          {p.company || "—"}
        </Text>
      ),
    },
    {
      key: "purchase_price",
      label: "Purchase price",
      align: "right",
      sortable: true,
      render: (p) => <Text size="sm">{formatCurrency(p.purchase_price)}</Text>,
    },
    {
      key: "list_price",
      label: "Sell price",
      align: "right",
      sortable: true,
      render: (p) => <Text size="sm">{formatCurrency(p.list_price)}</Text>,
    },
    {
      key: "business_charge",
      label: "Business charge",
      align: "right",
      render: (p) => (
        <Text size="sm" fw={500}>
          {formatCurrency(p.business_charge ?? 0)}
        </Text>
      ),
    },
  ];

  return (
    <>
      <StandardTable<Product>
        columns={columns}
        data={rows}
        getRowKey={(p) => p.id}
        minWidth={760}
        maxHeight="55dvh"
        stickyHeader
        ariaLabel="Product business charges"
        sortField={sortField}
        sortDirection={sortDirection}
        onSort={onSort}
        emptyState={
          <Text c="dimmed" size="sm">
            This salon has no products yet.
          </Text>
        }
      />
      <BusinessChargeEditModal<Product>
        opened={editOpen}
        onClose={onCloseEdit}
        title="Edit product business charges"
        rows={rows}
        getRowKey={(p) => p.id}
        getName={(p) => p.name}
        getCurrentCharge={(p) => p.business_charge ?? 0}
        contextColumns={modalContextColumns}
        onSave={handleSave}
        minWidth={640}
      />
    </>
  );
};

interface BusinessChargesTabProps {
  /** Catalog shown on first render; the dropdown switches it. Defaults to
   *  services. (Primarily a testing seam for the products view.) */
  initialCatalog?: Catalog;
}

const BusinessChargesTab: React.FC<BusinessChargesTabProps> = ({
  initialCatalog = "services",
}) => {
  const [catalog, setCatalog] = useState<Catalog>(initialCatalog);
  const [editOpen, setEditOpen] = useState(false);

  const { services } = useSalon() as { services: SalonService[] | null };
  const { products } = useProducts();
  const perms = usePermissions();
  const canEdit = perms?.settingsCommissions?.canEdit !== false;

  const activeEmpty =
    catalog === "services" ? !services?.length : !products?.length;

  // Switching catalog abandons any in-progress edit on the previous one.
  const handleCatalogChange = (value: string | null): void => {
    if (!value) return;
    setCatalog(value as Catalog);
    setEditOpen(false);
  };

  return (
    <Stack gap="md">
      <Text size="sm" c="dimmed">
        {INTRO}
      </Text>

      <Group justify="space-between" align="center">
        <Select
          data={CATALOG_OPTIONS}
          value={catalog}
          onChange={handleCatalogChange}
          allowDeselect={false}
          w={180}
          aria-label="Catalog"
        />
        {canEdit && (
          <Button
            variant="light"
            color="var(--brand-teal)"
            leftSection={<IconPencil size={16} />}
            onClick={() => setEditOpen(true)}
            disabled={activeEmpty}
          >
            Edit
          </Button>
        )}
      </Group>

      {catalog === "services" ? (
        <ServiceChargesTable
          editOpen={editOpen}
          onCloseEdit={() => setEditOpen(false)}
        />
      ) : (
        <ProductChargesTable
          editOpen={editOpen}
          onCloseEdit={() => setEditOpen(false)}
        />
      )}
    </Stack>
  );
};

export default BusinessChargesTab;
